import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { ArrowLeft, Check, Eye, EyeOff, Lock, Shield } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { authChangePassword } from '@/api/client';
import { AmazonConnectionsPanel } from '@/components/AmazonOauthConnections';
import { SignOutButton } from '@/components/auth/SignOutButton';
import { AuditTrailPanel } from '@/pages/settings/AuditTrailPanel';
import { cn } from '@/utils/cn';

function axiosErrorDetail(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

const pageShellClass = 'mx-auto w-full px-6 py-8 sm:px-8 lg:px-10';
const accountShellClass = 'max-w-5xl';
const auditShellClass = 'max-w-6xl';

type SettingsTab = 'account' | 'audit';

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'audit', label: 'Audit Trail' },
];

function parseTab(value: string | null): SettingsTab {
  return value === 'audit' ? 'audit' : 'account';
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  placeholder,
  minLength,
  helperText,
  visible,
  onToggleVisible,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  placeholder: string;
  minLength?: number;
  helperText?: string;
  visible: boolean;
  onToggleVisible: () => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-body-sm font-medium text-foreground">
        {label}
      </label>
      <div className="relative">
        <Lock
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-subtle"
          aria-hidden
        />
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          minLength={minLength}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 px-10"
        />
        <button
          type="button"
          onClick={onToggleVisible}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-foreground-muted hover:text-foreground"
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {helperText ? (
        <p className="mt-1.5 text-body-sm text-foreground-muted">{helperText}</p>
      ) : null}
    </div>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const tab = parseTab(searchParams.get('tab'));
  const isDemo = Boolean(user?.is_demo);

  const goBack = () => {
    navigate(-1);
  };

  const setTab = (next: SettingsTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'account') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className={cn(pageShellClass, 'space-y-6', tab === 'audit' ? auditShellClass : accountShellClass)}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <button
            type="button"
            onClick={goBack}
            className="p-2 rounded-xl hover:bg-surface-muted mt-0.5"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="mb-1 text-headline-lg font-semibold text-foreground">Settings</h1>
            <p className="text-body-sm text-foreground-muted">
              {tab === 'audit'
                ? 'Amazon campaign, ad, and budget changes made by users or automations.'
                : `Manage Amazon Ads connections and the password for ${user?.email ?? 'your account'}.`}
            </p>
          </div>
        </div>
        <SignOutButton withIcon />
      </div>

      <div className="flex gap-4 border-b border-border text-sm" role="tablist" aria-label="Settings">
        {TABS.map((item) => {
          const selected = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={cn(
                'border-b-2 px-1 pb-2',
                selected
                  ? 'border-brand text-foreground font-medium'
                  : 'border-transparent text-foreground-muted hover:text-foreground'
              )}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {tab === 'audit' ? (
        <AuditTrailPanel />
      ) : isDemo ? (
        <Card>
          <div className="p-6 sm:p-8">
            <p className="text-body-md text-foreground-muted">
              Demo accounts use a shared password and cannot be changed here.
            </p>
          </div>
        </Card>
      ) : (
        <AccountSettings />
      )}
    </div>
  );
}

function AccountSettings() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const mutation = useMutation({
    mutationFn: () => authChangePassword(currentPassword, newPassword),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      showToast('Password updated successfully', 'success');
    },
    onError: (e: unknown) => {
      setError(axiosErrorDetail(e));
    },
  });

  return (
    <div className="space-y-6">
      <AmazonConnectionsPanel />

      <Card>
        <div className="p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-foreground">Password</h2>
          <p className="mt-1 text-body-sm text-foreground-muted">
            Update the password for {user?.email ?? 'your account'}.
          </p>

          <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_16.5rem] lg:items-start">
            <form
              className="min-w-0 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                if (newPassword !== confirmPassword) {
                  setError('Passwords do not match');
                  return;
                }
                mutation.mutate();
              }}
            >
              <PasswordField
                id="current-password"
                label="Current password"
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
                placeholder="Enter your current password"
                visible={showCurrentPassword}
                onToggleVisible={() => setShowCurrentPassword((prev) => !prev)}
              />
              <PasswordField
                id="new-password"
                label="New password"
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
                placeholder="Enter your new password"
                minLength={8}
                helperText="At least 8 characters."
                visible={showNewPassword}
                onToggleVisible={() => setShowNewPassword((prev) => !prev)}
              />
              <PasswordField
                id="confirm-password"
                label="Confirm new password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                autoComplete="new-password"
                placeholder="Confirm your new password"
                minLength={8}
                visible={showConfirmPassword}
                onToggleVisible={() => setShowConfirmPassword((prev) => !prev)}
              />

              {error ? (
                <div className="rounded-xl border border-error/20 bg-error-subtle px-3 py-2 text-body-sm text-error-ink">
                  {error}
                </div>
              ) : null}

              <Button type="submit" disabled={mutation.isPending} className="h-10 px-4">
                {mutation.isPending ? 'Updating…' : 'Update password'}
              </Button>
            </form>

            <aside className="rounded-xl bg-brand-subtle px-5 py-5">
              <Shield className="h-5 w-5 text-brand-deep" aria-hidden />
              <p className="mt-3 font-semibold text-foreground">Keep your account secure</p>
              <p className="mt-1.5 text-body-sm text-foreground-muted">
                Use a strong password that you don&apos;t use on other sites.
              </p>
              <ul className="mt-4 space-y-2.5">
                {['At least 8 characters', 'Mix of letters, numbers & symbols', 'Avoid common passwords'].map(
                  (item) => (
                    <li key={item} className="flex items-start gap-2 text-body-sm text-foreground">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
                      <span>{item}</span>
                    </li>
                  )
                )}
              </ul>
            </aside>
          </div>
        </div>
      </Card>
    </div>
  );
}
